import { useState } from "react";
import { FileText, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Reveal, SectionHead } from "../motion";
import { SAMPLE_FINDINGS } from "../../content";

function SampleReportModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sample-report-modal" className="max-h-[85vh] max-w-2xl overflow-y-auto bg-ivory">
        <DialogHeader>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-cherry">
            Illustrative sample, entirely fictional
          </p>
          <DialogTitle className="font-display text-2xl text-ink">AML Health Check, sample report</DialogTitle>
          <DialogDescription className="text-sm text-ink/65">
            Prepared for <span className="font-medium">Hartley &amp; Co, Chartered Accountants</span>, a fictional
            small UK accountancy practice. This extract shows structure and style only; it is not based on an actual
            engagement and contains no real findings, data or recommendations.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          {SAMPLE_FINDINGS.map((f, i) => (
            <div key={f.area} data-testid={`sample-finding-${i}`} className="rounded-sm border border-ink/10 bg-warmwhite p-4">
              <div className="flex items-center justify-between gap-4">
                <p className="font-display text-base text-ink">{f.area}</p>
                <span className={`text-xs font-semibold uppercase tracking-[0.16em] ${f.tone}`}>{f.status}</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-ink/65">{f.note}</p>
            </div>
          ))}
        </div>

        <p className="mt-4 border-l-2 border-gold pl-4 text-xs leading-relaxed text-ink/60">
          A full Health Check report covers the risk assessment, policies, procedures and controls in scope, with
          findings prioritised by risk. It is an advisory review, not an audit or a regulatory inspection.
        </p>

        <button
          type="button"
          disabled
          data-testid="sample-report-download-button"
          className="mt-4 inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-ink/20 px-6 py-2.5 text-sm font-medium text-ink/45"
          title="A downloadable sample will be added soon"
        >
          <Lock className="h-4 w-4" aria-hidden="true" />
          PDF download, coming soon
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function SampleReport() {
  const [open, setOpen] = useState(false);

  return (
    <section id="sample-report" data-testid="sample-report-section" className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:py-24">
      <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        <div>
          <SectionHead
            eyebrow="Sample report"
            title="See What an AML Health Check Includes"
            intro="View an illustrative report showing how findings are presented, risks are prioritised and recommendations are documented."
          />
          <Reveal delay={0.15}>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="sample-report-preview-button"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-cherry px-7 py-3 text-sm font-medium text-ivory transition-colors duration-200 hover:bg-cherry-deep"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              View Sample Report
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative">
            <div className="absolute -inset-3 rotate-2 rounded-sm border border-gold/40" aria-hidden="true" />
            <article
              data-testid="sample-report-card"
              className="relative -rotate-1 rounded-sm border border-ink/10 bg-warmwhite p-6 text-ink shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)] transition-transform duration-500 hover:rotate-0 sm:p-7"
            >
              <span className="absolute right-5 top-5 rotate-6 rounded-sm border-2 border-cherry/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-cherry/60">
                Fictional
              </span>
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-cherry">
                <FileText className="h-4 w-4" aria-hidden="true" />
                Illustrative sample report
              </p>
              <h3 className="mt-4 font-display text-2xl">AML Health Check</h3>
              <p className="mt-1 text-sm text-ink/60">Prepared for Hartley &amp; Co, a fictional accountancy practice</p>

              <div className="mt-6 space-y-3" aria-hidden="true">
                {SAMPLE_FINDINGS.map((f) => (
                  <div key={f.area} className="flex items-center justify-between border-b border-ink/10 pb-2">
                    <span className="select-none text-xs text-ink/55 blur-[2px]">{f.area} review outcome</span>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.14em] ${f.tone}`}>{f.status}</span>
                  </div>
                ))}
              </div>

              <p className="mt-5 text-[11px] leading-relaxed text-ink/55">
                Entirely fictional and for illustration only. Not based on an actual engagement.
              </p>
            </article>
          </div>
        </Reveal>
      </div>

      <SampleReportModal open={open} onOpenChange={setOpen} />
    </section>
  );
}
